<template>
  <section class="equipment">
    <div v-if="combinedProducts.length > 0">
      <ul>
        <li
          v-for="item in combinedProducts"
          :key="item.product.product_code"
          class="equipment__list"
        >
          <div class="equipment__head">
            <span class="equipment__title">
              {{
                $t(
                  `descriptions.equipmentTypes.${item.product.product_code}.displayName`
                ) || item.product.name
              }}
            </span>
            <span class="equipment__actions">
              <template v-if="getProductPrice(item.product)">
                {{ getProductPrice(item.product) }}
                <template
                  v-if="getTotalProductCount(item.product.product_code) > 0"
                >
                  <button
                    class="no-button-style"
                    @click="handleRemoveProduct(item.product, direction)"
                    :aria-label="$t('common.actions.remove')"
                  >
                    -
                  </button>
                  <span class="equipment__count">
                    {{ getTotalProductCount(item.product.product_code) }}
                  </span>
                </template>
                <button
                  class="no-button-style"
                  @click="addProduct(item.product)"
                  :aria-label="$t('common.actions.add')"
                  :disabled="!item.isAvailable"
                >
                  <svg>
                    <use :xlink:href="`#${plus}`" />
                  </svg>
                </button>
              </template>
            </span>
          </div>
          <p
            v-if="getTotalProductCount(item.product.product_code) > 0"
            class="equipment__description"
          >
            {{
              $t(
                `descriptions.equipmentTypes.${item.product.product_code}.description`
              ) || ''
            }}
          </p>
        </li>
      </ul>
    </div>
    <div
      v-else-if="isLoading"
      class="equipment__loading"
    >
      {{ $t('common.status.loading') }}
    </div>
    <div
      v-else
      class="equipment__no-products"
    >
      {{ $t('travelPlanner.equipmentPage.noProducts') }}
    </div>
  </section>
</template>

<script lang="ts">
  import { defineComponent, PropType, computed } from 'vue'
  import plus from '@/assets/icons/plus.svg'
  import { useI18n } from 'vue-i18n'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { formatPrice } from '@/utils/formatters'
  import { cancelItems, patchProducts } from '@/services/journeyService'

  interface Product {
    product_code: string
    product_type: string
    name: string
    uuid?: string
    passenger_fares: {
      passenger_id: string
      price: {
        amount: number
        currency_code: string
      }
      original_price: {
        amount: number
        currency_code: string
      }
      inventory_class: string
      tariff_code: string
    }[]
    tariff_code?: string
    description?: string
    comfort_zone?: string
    discounts?: any[]
    max_quantity?: number
    cancelled?: boolean
  }

  interface AdditionalProduct {
    code: string
    name: string
    price: number
    item_ref?: number
    cancelled?: boolean
  }

  interface Segment {
    id: string
    service_identifier: string
    direction: 'outbound' | 'inbound'
    additional_products?: AdditionalProduct[]
  }

  interface BookingSegment extends Omit<Segment, 'additional_products'> {
    additional_products?: Array<AdditionalProduct>
  }

  interface BookingData {
    booking_number: string
    outbound_booking_tariff_segments: Segment[]
    inbound_booking_tariff_segments: Segment[]
  }

  type ExtendedBookingData = Omit<
    BookingData,
    'outbound_booking_tariff_segments' | 'inbound_booking_tariff_segments'
  > & {
    outbound_booking_tariff_segments: BookingSegment[]
    inbound_booking_tariff_segments: BookingSegment[]
  }

  interface BookingProduct extends Product {
    uuid: string
  }

  interface EquipmentItem {
    product_code: string
    passenger_id?: string
    count?: number
    uuid?: string
    product_type?: string
    name?: string
    tariff_code?: string
    price?: {
      amount: number
      currency_code: string
    }
    passenger_fares?: Array<{
      passenger_id: string
      price?: {
        amount: number
        currency_code: string
      }
      tariff_code?: string
    }>
  }

  export default defineComponent({
    name: 'Equipment',
    props: {
      products: {
        type: Array as PropType<Product[]>,
        required: true,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      leg: {
        type: Object as PropType<Segment>,
        required: true,
      },
      direction: {
        type: String as PropType<'outbound' | 'inbound'>,
        required: true,
      },
      selectedOutboundProducts: {
        type: Object as PropType<Record<string, number>>,
        required: true,
      },
      selectedInboundProducts: {
        type: Object as PropType<Record<string, number>>,
        required: true,
      },
    },
    emits: ['add-product', 'remove-product'],
    setup(props, { emit }) {
      const { t } = useI18n()
      const bookingStore = useBookingStore()
      const journeyStore = useJourneyStore()

      const booking = computed(() => bookingStore.bookingData)

      const isProductAvailableInAllSegments = (productCode: string) => {
        const segments =
          props.direction === 'outbound'
            ? booking.value?.outbound_booking_tariff_segments || []
            : booking.value?.inbound_booking_tariff_segments || []

        if (segments.length <= 1) return true

        return segments.every((segment: any) => {
          const segmentProducts = props.products.filter(
            (p) => p.product_code === productCode
          )
          return segmentProducts.length > 0
        })
      }

      const combinedProducts = computed(() => {
        const availableProducts = props.products.filter((product) => {
          let isCancelled = false
          if (typeof product.cancelled === 'boolean') {
            isCancelled = product.cancelled === true
          } else if (typeof product.cancelled === 'string') {
            isCancelled =
              product.cancelled === 'true' || product.cancelled === '1'
          } else if (typeof product.cancelled === 'number') {
            isCancelled = product.cancelled === 1
          }

          const isAvailable = !isCancelled &&
            product.product_type !== 'SF' &&
            isProductAvailableInAllSegments(product.product_code)
          return isAvailable
        })

        const uniqueProducts = availableProducts.reduce((acc, product) => {
          if (!acc.some((p) => p.product_code === product.product_code)) {
            acc.push(product)
          }
          return acc
        }, [] as Product[])

        const selectedProducts =
          props.direction === 'outbound'
            ? props.selectedOutboundProducts
            : props.selectedInboundProducts

        const result = uniqueProducts.map((product) => {
          const productCode = product.product_code || product.code
          const count = selectedProducts[productCode] || 0
          return {
            product,
            isAvailable: true,
            count
          }
        })

        return result
      })

      const getSegments = () => {
        return props.direction === 'outbound'
          ? booking.value?.outbound_booking_tariff_segments || []
          : booking.value?.inbound_booking_tariff_segments || []
      }

      const isTransferJourney = computed(() => {
        const segments = getSegments()
        return segments.length > 1
      })

      const getBookingProductCount = (productCode: string) => {
        const bookingProducts = bookingStore.bookingEquipment[props.direction]
        
        const product = bookingProducts?.find((e) => {
          if (e.product_code !== productCode) return false

          const cancelled = (e as any).cancelled
          
          if (cancelled === undefined) return true

          if (typeof cancelled === 'boolean') return !cancelled
          if (typeof cancelled === 'string')
            return cancelled !== 'true' && cancelled !== '1'
          if (typeof cancelled === 'number') return cancelled !== 1

          return true 
        })
                
        const segments = getSegments()
        const count = product?.count || 0
        const finalCount = isTransferJourney.value
          ? Math.ceil(count / segments.length)
          : count
          
        return finalCount
      }

      const getSelectedProductCount = (productCode: string) => {
        const selectedProducts =
          props.direction === 'outbound'
            ? props.selectedOutboundProducts
            : props.selectedInboundProducts
        
        const segments = getSegments()
        const count = selectedProducts[productCode] || 0
        const finalCount = isTransferJourney.value
          ? Math.ceil(count / segments.length)
          : count
          
        return finalCount
      }

      const getTotalProductCount = (productCode: string) => {
        const isRebooking = bookingStore.isRebooking

        if (isRebooking) {
          const selectedCount = getSelectedProductCount(productCode)
          const bookingCount = getBookingProductCount(productCode)
          
          if (selectedCount > 0) {
            return selectedCount
          }
          return bookingCount
        } else {
          const bookingCount = getBookingProductCount(productCode)
          const selectedCount = getSelectedProductCount(productCode)
          
          if (bookingCount > 0) {
            return bookingCount
          }
          return selectedCount
        }
      }

      const addProduct = async (product: Product) => {
        const currentCount = getTotalProductCount(product.product_code)
        const maxQuantity = product.max_quantity || 99

        if (currentCount >= maxQuantity) {
          return
        }
        emit('add-product', product)
      }

      const handleRemoveProduct = async (product: Product, direction: 'outbound' | 'inbound') => {
        try {
          const productCode = product.product_code
          const productType = product.product_type || (product as any).type
          const bookingNumber = journeyStore.bookingData?.booking_number || bookingStore.bookingNumber

          const EQUIPMENT_CODES = ['DG', 'BK', 'FB', 'LU', 'PR', 'VC', 'IN', 'ET', 'DS', 'PE', 'HO', 'WI', 'MD', 'ME', 'TA', 'PA', 'LT', 'AU']

          const bookingSegments = bookingStore.bookingData?.[direction === 'outbound' ? 'outbound_booking_tariff_segments' : 'inbound_booking_tariff_segments'] || []
          const allAdditionalProducts = bookingSegments.flatMap(s => s.additional_products || [])
          const apiProducts = allAdditionalProducts.filter(p => {
            const codeMatch = (p.code === productCode)
            
            const uuidMatch = product.uuid && p.uuid === product.uuid
            
            const typeMatch = (p.type === productType)

            const isKnownEquipmentType = p.type && EQUIPMENT_CODES.includes(p.type)
            
            return (codeMatch || uuidMatch || (typeMatch && isKnownEquipmentType)) && typeof p.item_ref === 'number'
          })
          
          const apiProduct = apiProducts.length > 0 ? apiProducts[0] : null

          if (apiProducts.length > 0) {
            const itemRefs = apiProducts.map(p => p.item_ref).filter(ref => typeof ref === 'number')

            if (!bookingNumber) {
              emit('remove-product', product)
              return
            }
            
            const cancelResult = await cancelItems(bookingNumber, itemRefs)

            await bookingStore.fetchBookingData(bookingNumber)
            
            const productsToUpdate = bookingStore.bookingEquipment[direction]?.filter(
              e => {
                const uuidMatch = product.uuid && e.uuid === product.uuid;
                const codeMatch = e.product_code === productCode;
                const itemRefMatch = itemRefs.includes((e as any).item_ref);
                
                return uuidMatch || codeMatch || itemRefMatch;
              }
            )

            if (productsToUpdate && productsToUpdate.length > 0) {
              productsToUpdate.forEach(p => {
                ;(p as any).cancelled = true
              })
            }
            
            await bookingStore.setBookingData(bookingStore.bookingData)
            
            if (direction === 'outbound') {
              bookingStore.refreshOutboundEquipment()
            } else {
              bookingStore.refreshInboundEquipment()
            }
            
            emit('remove-product', product)
          } else {
            if (direction === 'outbound') {
              bookingStore.removeOutboundEquipment({
                product_code: productCode,
                uuid: product.uuid || '',
                product_type: productType,
                count: 1,
                name: product.name || '',
                tariff_code: product.tariff_code || '',
                price: { amount: 0, currency_code: 'NOK' },
                passenger_fares: [
                  {
                    passenger_id: product.passenger_fares?.[0]?.passenger_id || '',
                    price: { amount: 0, currency_code: 'NOK' },
                  },
                ],
              })
            } else {
              bookingStore.removeInboundEquipment({
                product_code: productCode,
                uuid: product.uuid || '',
                product_type: productType,
                count: 1,
                name: product.name || '',
                tariff_code: product.tariff_code || '',
                price: { amount: 0, currency_code: 'NOK' },
                passenger_fares: [
                  {
                    passenger_id: product.passenger_fares?.[0]?.passenger_id || '',
                    price: { amount: 0, currency_code: 'NOK' },
                  },
                ],
              })
            }

            emit('remove-product', product)
          }
        } catch (error) {
          console.error('[Equipment] Error removing product:', error)
        }
      }

      const formatPriceWithCurrency = (price: number) => {
        if (!price || typeof price !== 'number') return ''

        return formatPrice(price)
      }

      const getProductPrice = (product: Product) => {
        let priceAmount = product.passenger_fares?.[0]?.price?.amount || product.price?.amount || 0
        return formatPriceWithCurrency(priceAmount)
      }

      return {
        t,
        plus,
        combinedProducts,
        getBookingProductCount,
        getSelectedProductCount,
        getTotalProductCount,
        addProduct,
        handleRemoveProduct,
        formatPriceWithCurrency,
        getProductPrice,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .equipment {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    h2 {
      @include title-medium;
      text-align: left;
      color: $clr-primary-800;
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background-color: $clr-white;
        color: $clr-primary-800;
        border-radius: $border-radius;
        padding: 1rem;
        margin-bottom: 0.75rem;
        box-shadow: 0px 0px 4px 1px #2a347a26;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    &__head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      font-weight: 500;

      > span {
        display: flex;
        align-items: center;
        margin-left: 1rem;
        gap: 1rem;
      }
    }

    &__actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
    }

    &__count {
      min-width: 1.5rem;
      text-align: center;
    }

    &__description {
      text-align: left;
      margin: 0.5rem 0 0;
      font-size: 0.875rem;
      color: var(--color-text-secondary);
    }

    &__loading,
    &__no-products {
      text-align: center;
      padding: 2rem;
      color: var(--color-text-secondary);
    }

    .no-button-style {
      background-color: transparent;
      color: $clr-primary-400;
      cursor: pointer;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      border: 1px solid $clr-primary-400;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        height: 24px;
        width: 24px;
      }
    }
  }
</style>
